import React, { useState, useEffect } from 'react';
import { Card, Image, message, Spin, Form, Button, Row, Col, Select, Input, DatePicker, InputNumber, Tabs, Descriptions, Upload, Alert, Pagination, Modal, Popconfirm, Switch } from 'antd';
import { useAuth } from '../contexts/AuthContext';
import { PlusOutlined, PictureOutlined, ReloadOutlined, StarFilled } from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import BMIScale from './BMIScale';

const { Option } = Select;

const StageEvaluationComponent = ({ memberId, memberInfo, onMemberChange, phase }) => {
  // 设置 dayjs 为中文
  dayjs.locale('zh-cn');
  const { apiClient } = useAuth();
  
  // 阶段测评相关状态
  const [evaluationList, setEvaluationList] = useState([]);
  const [evaluationForm] = Form.useForm();
  const [evaluationLoading, setEvaluationLoading] = useState(false);
  const [evaluationImages, setEvaluationImages] = useState([]);
  const [evaluationActiveTab, setEvaluationActiveTab] = useState('1');
  const [isEvaluationEditMode, setIsEvaluationEditMode] = useState(false);
  // 新增状态用于分页和日期筛选
  const [evaluationPagination, setEvaluationPagination] = useState({ current: 1, pageSize: 1, total: 0 });
  const [evaluationSearchParams, setEvaluationSearchParams] = useState({ searchDate: null });
  // BMI缓存
  const [bmiCache, setBmiCache] = useState(new Map());
  
  // 当前选中的会员
  const [selectedMember, setSelectedMember] = useState(null);

  // HTML解码函数
  const decodeHtml = (html) => {
    const txt = document.createElement('textarea');
    txt.innerHTML = html;
    return txt.value;
  };

  // 将纯文本或旧格式统一转成安全的 HTML，用于展示模式
  const normalizeRichText = (raw) => {
    if (!raw) return '';
    let content = String(raw);
    // 先进行 HTML 解码，兼容后端存的转义内容
    try {
      content = decodeHtml(content);
    } catch (e) {
      // ignore
    }
    const trimmed = content.trim();
    if (!trimmed) return '';

    // 简单判断是否已经是 HTML 结构（包括 Quill 生成的内容）
    const htmlLikePattern = /<([a-zA-Z][^>]*)>/;
    if (
      htmlLikePattern.test(trimmed) ||
      /&nbsp;/.test(trimmed) ||
      trimmed.startsWith('<p') ||
      trimmed.startsWith('<div')
    ) {
      return trimmed;
    }

    // 简单文本：需要做转义并按换行转成 <p>/<br>
    const escaped = trimmed
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    const lines = escaped.split(/\r?\n/);
    if (lines.length === 1) {
      return `<p>${lines[0] || ''}</p>`;
    }
    return lines
      .map((line) => `<p>${line || ''}</p>`)
      .join('');
  };

  // BMI Assessment Component
  const BMIAssessment = ({ item, member, apiClient, bmiCache, setBmiCache }) => {
    const [bmiData, setBmiData] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
      const fetchBMI = async () => {
        if (!member || !item) return;
        
        const cacheKey = `${member.id}-${item.evaluationDate}-${item.height}-${item.weight}`;
        if (bmiCache.has(cacheKey)) {
          setBmiData(bmiCache.get(cacheKey));
          return;
        }
        if (!item.height || !item.weight || !member.babyBirthday || !member.babySex || !item.evaluationDate) {
          setBmiData(null);
          return;
        }
        setLoading(true);
        try {
          const params = {
            sex: parseInt(member.babySex),
            birthDate: member.babyBirthday,
            evaluationDate: item.evaluationDate,
            height: parseFloat(item.height),
            weight: parseFloat(item.weight),
          };
          const response = await apiClient.post('/sports-server-prod/taller.api.v1/hw/bmi/adolescents/data', params);
          if (response.data.code === 200) {
            setBmiData(response.data.data);
            setBmiCache(prev => new Map(prev).set(cacheKey, response.data.data));
          } else {
            setBmiData(null);
          }
        } catch (error) {
          console.error('Failed to fetch BMI data:', error);
          setBmiData(null);
        } finally {
          setLoading(false);
        }
      };
      fetchBMI();
    }, [item.height, item.weight, item.evaluationDate, member?.babyBirthday, member?.babySex, apiClient, bmiCache, setBmiCache]);

    if (loading) {
      return <Spin />;
    }

    if (!bmiData) {
      return <div>无法获取BMI数据</div>;
    }

    const thresholds = bmiData.bands.map(band => ({
      key: band.label,
      label: band.label,
      start: band.start,
      end: band.end,
      color: band.label === '中重度消瘦' ? '#f5222d' :
             band.label === '轻度消瘦' ? '#fa8c16' :
             band.label === '正常' ? '#52c41a' :
             band.label === '超重' ? '#faad14' :
             band.label === '肥胖' ? '#f5222d' : '#52c41a',
    }));

    return (
      <BMIScale
        value={bmiData.userBmi}
        thresholds={thresholds}
        title="BMI 体重指数（中国儿童青少年0~18岁生成标准）"
        unit="kg/m²"
        showTicks
        tickStep={1}
        style={{ marginTop: 8, width: '100%', display: 'block' }}
      />
    );
  };

  // 初始化会员信息
  useEffect(() => {
    if (memberId && memberInfo) {
      setSelectedMember({
        ...memberInfo,
        id: memberId
      });
      // 自动加载该会员的测评历史
      openEvaluationModal({
        ...memberInfo,
        id: memberId
      });
    }
  }, [memberId, memberInfo, phase]);

  // 打开阶段测评详情
  const openEvaluationModal = async (member, pageNum = 1, searchDate = null) => {
    setSelectedMember(member);
    setEvaluationList([]);
    setEvaluationImages([]);
    evaluationForm.resetFields();
    evaluationForm.setFieldsValue({
      babyName: member.babyName,
      evaluationDate: dayjs(),
    });
    // 重置tab到第一个
    setEvaluationActiveTab('1');
    // 重置为新增模式
    setIsEvaluationEditMode(false);

    try {
      setEvaluationLoading(true);
      const params = {
        babyId: member.id,
        pageSize: 1,
        pageNo: pageNum,
      };
      
      // 如果有阶段参数，添加到请求参数
      if (phase !== undefined && phase !== null) {
        params.phase = Number(phase);
      }
      
      // 如果有指定日期，则添加日期筛选
      if (searchDate) {
        params.searchDate = searchDate.format('YYYY-MM-DD');
      }

      const response = await apiClient.post('/sports-server-prod/taller.api.v1/evaluation/pageList', params);

      if (response.data && response.data.code === 200) {
        setEvaluationList(response.data.data?.list || []);
        setEvaluationPagination({
          current: pageNum,
          pageSize: 1,
          total: response.data.data?.totalRecord || 0
        });
        setEvaluationSearchParams({ searchDate });
      } else {
        message.error(response.data?.msg || '获取测评历史失败');
      }
    } catch (error) {
      console.error('获取测评历史失败:', error);
      message.error('获取测评历史失败');
    } finally {
      setEvaluationLoading(false);
    }
  };

  // 处理测评分页变化
  const handleEvaluationPageChange = (page) => {
    openEvaluationModal(
      selectedMember, 
      page, 
      evaluationSearchParams.searchDate
    );
  };

  // 处理日期筛选（测评历史tab的日期选择）
  const handleEvaluationHistoryDateChange = (date) => {
    // 重新加载列表
    openEvaluationModal(
      selectedMember, 
      1, 
      date
    );
  };

  // 处理日期筛选（编辑tab的日期选择）
  const handleEvaluationDateChange = (date) => {
    setEvaluationSearchParams({ searchDate: date });
  };

  // 打开修改测评的标签页
  const openEditEvaluationTab = (evaluation) => {
    // 设置表单值
    evaluationForm.setFieldsValue({
      id: evaluation.id,  // 添加id字段
      babyName: selectedMember.babyName,
      evaluationDate: dayjs(evaluation.evaluationDate),
      height: evaluation.height,
      weight: evaluation.weight,
      heartRate: evaluation.heartRate,
      boneAge: evaluation.boneAge,
      briefSummary: evaluation.briefSummary,
      suggestion: evaluation.suggestion,
      trainTimes: evaluation.trainTimes,
      isExcellent: evaluation.isExcellent === "1" ? "1" : "0",
    });
    
    // 设置图片列表
    const images = (evaluation.detailList || []).map((img, index) => ({
      uid: -index,
      name: `image-${index}.jpg`,
      status: 'done',
      url: img.phoneAddress,
      thumbUrl: img.phoneAddress,
      originFileObj: null,
    }));
    setEvaluationImages(images);
    
    // 更新日期搜索参数
    setEvaluationSearchParams({ searchDate: dayjs(evaluation.evaluationDate) });
    
    // 设置为编辑模式
    setIsEvaluationEditMode(true);
    
    // 切换到第二个tab
    setEvaluationActiveTab('2');
  };

  // 切换优秀案例状态
  const handleToggleExcellent = async (item, checked) => {
    try {
      setEvaluationLoading(true);
      // 调用修改接口，只更新isExcellent字段
      const payload = {
        id: item.id,
        isExcellent: checked ? "1" : "0",
      };
      
      const resp = await apiClient.post('/sports-server-prod/taller.api.v1/evaluation/add', payload);
      
      if (resp.data && resp.data.code === 200) {
        // 根据操作状态给出不同的提示
        const successMessage = checked ? '标记成功' : '取消成功';
        message.success(`优秀案例${successMessage}`);
        // 直接更新本地状态，无需重新加载整个列表
        setEvaluationList(prevList => 
          prevList.map(evalItem => 
            evalItem.id === item.id 
              ? { ...evalItem, isExcellent: checked ? "1" : "0" }
              : evalItem
          )
        );
      } else {
        message.error(resp.data?.msg || '操作失败');
      }
    } catch (error) {
      console.error('标记优秀案例失败:', error);
      message.error('标记失败，请稍后重试');
    } finally {
      setEvaluationLoading(false);
    }
  };

  // 删除阶段测评详情
  const handleDeleteEvaluationDetail = async (detailId) => {
    try {
      setEvaluationLoading(true);
      const response = await apiClient.post(`/sports-server-prod/taller.api.v1/evaluation/delete?id=${detailId}`);
      
      if (response.data && response.data.code === 200) {
        message.success('删除成功');
        // 重新加载测评历史
        await openEvaluationModal(selectedMember, 1, null);
      } else {
        message.error(response.data?.msg || '删除失败');
      }
    } catch (error) {
      console.error('删除测评详情失败:', error);
      message.error('删除失败');
    } finally {
      setEvaluationLoading(false);
    }
  };

  // 切换到新增模式
  const switchToNewEvaluationMode = () => {
    // 重置表单
    evaluationForm.resetFields();
    evaluationForm.setFieldsValue({
      babyName: selectedMember.babyName,
      evaluationDate: dayjs(),
    });
    // 清空图片
    setEvaluationImages([]);
    // 清空日期搜索参数
    setEvaluationSearchParams({ searchDate: null });
    // 设置为新增模式
    setIsEvaluationEditMode(false);
  };

  // 保存阶段测评
  const handleSaveEvaluation = async () => {
    try {
      const values = await evaluationForm.validateFields();

      if (!selectedMember) {
        message.error('未找到当前会员信息');
        return;
      }

      // 处理图片 - 已在上传时获取到URL，直接提取
      const uploadedImages = [];
      for (const file of evaluationImages) {
        if (file.response && file.response.data) {
          // 新上传的图片，从响应中获取URL
          const data = file.response.data;
          const key = Object.keys(data)[0];
          const value = data[key];
          uploadedImages.push({ phoneAddress: value });
        } else if (file.url) {
          // 已存在的图片（编辑模式）
          uploadedImages.push({ phoneAddress: file.url });
        }
      }

      const userStr = localStorage.getItem('user') || '{}';
      const user = JSON.parse(userStr);

      const payload = {
        babyId: selectedMember.id,
        babyName: selectedMember.babyName,
        evaluationDate: values.evaluationDate.format('YYYY-MM-DD'),
        trainTimes: values.trainTimes || 1,
        height: values.height,
        weight: values.weight,
        heartRate: values.heartRate,
        boneAge: values.boneAge,
        briefSummary: values.briefSummary,
        suggestion: values.suggestion,
        isExcellent: values.isExcellent || '0',
        createUser: user.name || '',
        updateUser: user.name || '',
        id: values.id || '',
        detailList: uploadedImages,
      };
      
      // 如果有阶段参数，添加到保存参数
      if (phase !== undefined && phase !== null) {
        payload.phase = Number(phase);
      }

      setEvaluationLoading(true);
      const resp = await apiClient.post('/sports-server-prod/taller.api.v1/evaluation/add', payload);
      if (resp.data && resp.data.code === 200) {
        message.success('测评保存成功');
        // 重置tab到第一个
        setEvaluationActiveTab('1');
        // 重置为新增模式
        setIsEvaluationEditMode(false);
        // 重新获取测评历史
        await openEvaluationModal(selectedMember, 1, null);
      } else {
        message.error(resp.data?.msg || '测评保存失败');
      }
    } catch (error) {
      if (error.errorFields) {
        return;
      }
      console.error('保存测评失败:', error);
      message.error('保存测评失败');
    } finally {
      setEvaluationLoading(false);
    }
  };

  // 图片预览
  const handlePreview = async (file) => {
    let src = file.url;
    if (!src) {
      src = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(file.originFileObj);
        reader.onload = () => resolve(reader.result);
      });
    }
    // 使用window.open直接打开图片
    window.open(src, '_blank');
  };

  // 添加生成趋势相关状态
  const [trendModalVisible, setTrendModalVisible] = useState(false);
  const [trendLoading, setTrendLoading] = useState(false);
  const [trendError, setTrendError] = useState(null);
  const [trendImageSrc, setTrendImageSrc] = useState(null);
  const [trendTargetMember, setTrendTargetMember] = useState(null);

  // 生成趋势图表函数
  const fetchTrendChart = async (member) => {
    if (!apiClient) {
      message.error('系统初始化中，请稍后重试');
      return;
    }

    const babyId = member.id;
    const birthDate = member.babyBirthday;
    const sex = member.babySex;

    if (!babyId) {
      setTrendError('会员ID缺失，无法生成趋势图');
      return;
    }

    setTrendLoading(true);
    setTrendError(null);
    setTrendImageSrc(null);

    try {
      const payload = {
        sex: String(sex || '1'), // 默认为男性
        hwType: '01',
        userInfo: {
          babyId: babyId,
          birthDate: dayjs(birthDate).format('YYYY-MM-DD'),
        },
      };

      const resp = await apiClient.post(
        '/sports-server-prod/taller.api.v1/hw/percentile/chart',
        payload,
        {
          responseType: 'arraybuffer',
          headers: {
            Accept: 'image/png',
          },
        }
      );

      if (!resp || !resp.data) {
        throw new Error('未获取到图片数据');
      }

      const contentType = resp.headers && (resp.headers['content-type'] || resp.headers['Content-Type']);
      const mimeType = contentType && typeof contentType === 'string' ? contentType.split(';')[0] : 'image/png';

      const blob = new Blob([resp.data], { type: mimeType || 'image/png' });
      const imageUrl = URL.createObjectURL(blob);
      setTrendImageSrc(imageUrl);
    } catch (e) {
      console.error('获取趋势图失败:', e);
      message.error('生成趋势图失败，请稍后重试');
      setTrendError(e?.response?.data?.msg || '生成趋势图失败，请稍后重试');
    } finally {
      setTrendLoading(false);
    }
  };

  // 打开趋势图弹窗
  const openTrendModal = (member) => {
    // 清理上一次的结果
    if (trendImageSrc) {
      try {
        URL.revokeObjectURL(trendImageSrc);
      } catch (e) {
        // ignore
      }
    }
    setTrendTargetMember(member);
    setTrendError(null);
    setTrendImageSrc(null);
    setTrendModalVisible(true);
    fetchTrendChart(member);
  };

  // 关闭趋势图弹窗
  const closeTrendModal = () => {
    if (trendImageSrc) {
      try {
        URL.revokeObjectURL(trendImageSrc);
      } catch (e) {
        // ignore
      }
    }
    setTrendModalVisible(false);
    setTrendTargetMember(null);
    setTrendError(null);
    setTrendImageSrc(null);
    setTrendLoading(false);
  };

  // 当会员信息发生变化时的回调
  const handleMemberInfoChange = (newMemberInfo) => {
    if (onMemberChange) {
      onMemberChange(newMemberInfo);
    }
  };

  return (
    <div className="stage-evaluation-component">
      <Card 
        title={phase !== undefined && phase !== null ? `第 ${phase} 阶段测评` : "阶段测评"} 
        style={{ margin: '16px' }}
        extra={
          selectedMember && (
            <Button.Group>
              <Button 
                type="default"
                icon={<ReloadOutlined />}
                onClick={() => openEvaluationModal(selectedMember, 1, null)}
              >
                刷新
              </Button>
              <Button 
                type="default"
                icon={<PictureOutlined />}
                onClick={() => openTrendModal(selectedMember)}
              >
                生长趋势
              </Button>
            </Button.Group>
          )
        }
      >
        {selectedMember ? (
          <div>
            <div style={{ marginBottom: 16 }}>
              <span style={{ marginRight: 16 }}>当前会员: <strong>{selectedMember.babyName}</strong></span>
              <span>会员ID: {selectedMember.id}</span>
            </div>

            <Tabs defaultActiveKey="1" activeKey={evaluationActiveTab} onChange={setEvaluationActiveTab}>
              <Tabs.TabPane tab="测评历史" key="1">
                <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
                  <DatePicker
                    placeholder="选择日期"
                    onChange={handleEvaluationHistoryDateChange}
                    value={evaluationSearchParams.searchDate}
                    allowClear
                  />
                </div>
                
                <Spin spinning={evaluationLoading}>
                  {evaluationList.length > 0 ? (
                    evaluationList.map((item, index) => (
                      <Card 
                        key={item.id || index} 
                        style={{ marginBottom: 16 }}
                        title={
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <span style={{ fontSize: '16px', fontWeight: '500' }}>优秀案例</span>
                            <Switch 
                              checked={item.isExcellent === "1"}
                              onChange={(checked) => handleToggleExcellent(item, checked)}
                              size="default"
                            />
                          </div>
                        }
                        extra={
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Button 
                              type="primary" 
                              size="small" 
                              onClick={() => openEditEvaluationTab(item)}
                              style={{ marginRight: 8 }}
                            >
                              修改
                            </Button>
                            <Popconfirm
                              title="确定要删除这条测评记录吗？"
                              onConfirm={() => handleDeleteEvaluationDetail(item.id)}
                              okText="确定"
                              cancelText="取消"
                            >
                              <Button 
                                type="danger" 
                                size="small" 
                                danger
                              >
                                删除
                              </Button>
                            </Popconfirm>
                          </div>
                        }
                      >
                        <Descriptions 
                          title={
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span>测评日期: {item.evaluationDate}</span>
                              <div>
                                {item.lastEvaluationDate && (
                                  <span style={{ marginRight: 16 }}>上次测评时间: {item.lastEvaluationDate}</span>
                                )}
                                {item.heightThanLastTime && parseFloat(item.heightThanLastTime) > 0 && (
                                  <span>
                                    比上次增高: <span style={{ color: '#52c41a', marginRight: 4 }}>↑</span>{(parseFloat(item.heightThanLastTime) * 10).toFixed(1)} 毫米
                                  </span>
                                )}
                              </div>
                            </div>
                          } 
                          bordered 
                          column={5}
                        >
                          <Descriptions.Item label="身高" span={1}>{item.height} cm</Descriptions.Item>
                          <Descriptions.Item label="体重" span={1}>{item.weight} kg</Descriptions.Item>
                          <Descriptions.Item label="心率" span={1}>{item.heartRate} 次/分</Descriptions.Item>
                          <Descriptions.Item label="骨龄" span={1}>{item.boneAge} 岁</Descriptions.Item>
                          <Descriptions.Item label="训练次数" span={1}>{item.trainTimes} 次</Descriptions.Item>
                          <Descriptions.Item label="体重评估" span={5}>
                            <BMIAssessment item={item} member={selectedMember} apiClient={apiClient} bmiCache={bmiCache} setBmiCache={setBmiCache} />
                          </Descriptions.Item>
                          <Descriptions.Item label="测评小结" span={5}>
                            <div dangerouslySetInnerHTML={{ __html: item.briefSummary }} />
                          </Descriptions.Item>
                          <Descriptions.Item label="专家建议" span={5}>
                            <div dangerouslySetInnerHTML={{ __html: item.suggestion }} />
                          </Descriptions.Item>
                          {item.detailList && item.detailList.length > 0 && (
                            <Descriptions.Item label="测评图片" span={5}>
                              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                {item.detailList.map((img, imgIndex) => (
                                  <Image
                                    key={imgIndex}
                                    src={img.phoneAddress}
                                    alt={`测评图片${imgIndex + 1}`}
                                    width={100}
                                    height={100}
                                    style={{ objectFit: 'cover' }}
                                  />
                                ))}
                              </div>
                            </Descriptions.Item>
                          )}
                        </Descriptions>
                      </Card>
                    ))
                  ) : (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                      暂无测评历史
                    </div>
                  )}
                  
                  {evaluationPagination.total > 0 && false && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span>共 {evaluationPagination.total} 条记录</span>
                        <Pagination
                          current={evaluationPagination.current}
                          pageSize={evaluationPagination.pageSize}
                          total={evaluationPagination.total}
                          onChange={handleEvaluationPageChange}
                          showSizeChanger={false}
                          showQuickJumper={false}
                          size="small"
                        />
                      </div>
                    </div>
                  )}
                </Spin>
              </Tabs.TabPane>

              <Tabs.TabPane tab="新增/编辑测评" key="2">
                {isEvaluationEditMode && (
                  <div style={{ marginBottom: 16, textAlign: 'right' }}>
                    <Button 
                      type="default" 
                      onClick={switchToNewEvaluationMode}
                      icon={<PlusOutlined />}
                    >
                      切换到新增模式
                    </Button>
                  </div>
                )}
                <Form form={evaluationForm} layout="vertical">
                  {/* 隐藏的id字段，用于修改模式 */}
                  <Form.Item name="id" hidden>
                    <Input />
                  </Form.Item>
                  <Row gutter={16}>
                    <Col span={8}>
                      <Form.Item label="会员姓名" name="babyName">
                        <Input disabled />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        label="测评日期"
                        name="evaluationDate"
                        rules={[{ required: true, message: '请选择测评日期' }]}
                      >
                        <DatePicker 
                          style={{ width: '100%' }} 
                          format="YYYY-MM-DD" 
                          onChange={handleEvaluationDateChange}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        label="训练次数(次)"
                        name="trainTimes"
                        rules={[{ required: true, message: '请输入训练次数' }]}
                      >
                        <InputNumber 
                          min={0} 
                          precision={2}
                          style={{ width: '100%' }} 
                          parser={(value) => value.replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1')}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={8}>
                      <Form.Item
                        label="身高(cm)"
                        name="height"
                      >
                        <InputNumber 
                          min={0} 
                          precision={2}
                          style={{ width: '100%' }} 
                          parser={(value) => value.replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1')}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        label="体重(kg)"
                        name="weight"
                      >
                        <InputNumber 
                          min={0} 
                          precision={2}
                          style={{ width: '100%' }} 
                          parser={(value) => value.replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1')}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        label="心率(次/分)"
                        name="heartRate"
                      >
                        <InputNumber 
                          min={0} 
                          precision={2}
                          style={{ width: '100%' }} 
                          parser={(value) => value.replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1')}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={8}>
                      <Form.Item
                        label="骨龄(岁)"
                        name="boneAge"
                      >
                        <InputNumber 
                          min={0} 
                          precision={2}
                          style={{ width: '100%' }} 
                          parser={(value) => value.replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1')}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={24}>
                      <Form.Item
                        label="测评小结"
                        name="briefSummary"
                        rules={[
                          {
                            validator: (_, value) => {
                              if (!value) return Promise.resolve();
                              // 移除HTML标签后验证长度
                              const text = value.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
                              if (text.length > 200) {
                                return Promise.reject('测评小结最多输入200个字符');
                              }
                              return Promise.resolve();
                            }
                          }
                        ]}
                        validateTrigger="onBlur"
                      >
                        <ReactQuill
                          theme="snow"
                          modules={{
                            toolbar: [
                              [{ 'header': [1, 2, false] }],
                              ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                              [{'list': 'ordered'}, {'list': 'bullet'}, {'indent': '-1'}, {'indent': '+1'}],
                              ['link', 'image'],
                              ['clean']
                            ]
                          }}
                          formats={[
                            'header', 'bold', 'italic', 'underline', 'strike', 'blockquote',
                            'list', 'bullet', 'indent', 'link', 'image'
                          ]}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={24}>
                      <Form.Item
                        label="专家建议"
                        name="suggestion"
                        rules={[
                          {
                            validator: (_, value) => {
                              if (!value) return Promise.resolve();
                              // 移除HTML标签后验证长度
                              const text = value.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
                              if (text.length > 200) {
                                return Promise.reject('专家建议最多输入200个字符');
                              }
                              return Promise.resolve();
                            }
                          }
                        ]}
                        validateTrigger="onBlur"
                      >
                        <ReactQuill
                          theme="snow"
                          modules={{
                            toolbar: [
                              [{ 'header': [1, 2, false] }],
                              ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                              [{'list': 'ordered'}, {'list': 'bullet'}, {'indent': '-1'}, {'indent': '+1'}],
                              ['link', 'image'],
                              ['clean']
                            ]
                          }}
                          formats={[
                            'header', 'bold', 'italic', 'underline', 'strike', 'blockquote',
                            'list', 'bullet', 'indent', 'link', 'image'
                          ]}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={24}>
                      <Form.Item label="测评图片">
                        <Upload
                          listType="picture-card"
                          fileList={evaluationImages}
                          onChange={({ fileList }) => setEvaluationImages(fileList)}
                          onPreview={handlePreview}
                          accept="image/*"
                          customRequest={async ({ file, onSuccess, onError }) => {
                            const formData = new FormData();
                            formData.append('file', file);
                            try {
                              const response = await apiClient.post('/sports-server-prod/sys.api.v1/file/upload', formData, {
                                headers: {
                                  'Content-Type': 'multipart/form-data',
                                },
                              });
                              if (response.data && response.data.code === 200) {
                                onSuccess(response.data);
                              } else {
                                onError(new Error(response.data?.msg || '上传失败'));
                              }
                            } catch (error) {
                              onError(error);
                            }
                          }}
                        >
                          <div>
                            <PlusOutlined />
                            <div style={{ marginTop: 8 }}>上传图片</div>
                          </div>
                        </Upload>
                      </Form.Item>
                    </Col>
                  </Row>

                  <div style={{ textAlign: 'right', marginTop: 16 }}>
                    <Button onClick={() => {
                      evaluationForm.resetFields();
                      setEvaluationImages([]);
                    }} style={{ marginRight: 8 }}>
                      重置
                    </Button>
                    <Button type="primary" loading={evaluationLoading} onClick={handleSaveEvaluation}>
                      保存
                    </Button>
                  </div>
                </Form>
              </Tabs.TabPane>
            </Tabs>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div>未提供会员信息</div>
          </div>
        )}
      </Card>

      {/* 趋势图弹窗 */}
      <Modal
        title="生长发育趋势图"
        open={trendModalVisible}
        onCancel={closeTrendModal}
        footer={null}
        width={720}
      >
        {trendTargetMember && (
          <div style={{ marginBottom: 16 }}>
            <span style={{ marginRight: 16 }}>姓名：{trendTargetMember.babyName}</span>
            <span style={{ marginRight: 16 }}>性别：{trendTargetMember.babySex === '1' ? '男' : '女'}</span>
            <span>生日：{trendTargetMember.babyBirthday}</span>
          </div>
        )}
        {trendError && (
          <Alert
            type="error"
            message={trendError}
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
        <div style={{ minHeight: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {trendLoading && <Spin tip="生成中..." />}
          {!trendLoading && trendImageSrc && (
            <Image
              src={trendImageSrc}
              alt="生长发育趋势图"
              style={{ maxHeight: 600 }}
            />
          )}
          {!trendLoading && !trendImageSrc && !trendError && (
            <span>暂无数据</span>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default StageEvaluationComponent;

